/**
 * DivisionInput - A specialized input for scale readings
 *
 * Features:
 * - Constrains values to scale division increments
 * - Up/down arrow buttons for quick adjustment (desktop)
 * - Pre-populates with test load as hint (disappears on typing)
 * - Mobile scroll wheel picker on touch
 * - Warning for non-division-aligned manual entry
 * - Haptic feedback on mobile
 *
 * Props:
 * - value: Current reading value
 * - onChange: Callback when value changes
 * - testLoad: The applied test weight (used as default/hint)
 * - division: Scale's minimum division (e.g., "20 lb", 0.05, 0.001)
 * - unit: Unit of measurement (lb, kg, g)
 * - capacity: Scale capacity (for range limits)
 * - disabled: Whether input is disabled
 * - label: Optional label for accessibility
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  Warning as WarningIcon
} from '@mui/icons-material';
import DivisionScrollPicker from './DivisionScrollPicker';
import MobileTapStepInput from './MobileTapStepInput';
import {
  parseDivision,
  parseUnit,
  formatToDivision,
  isValidDivisionValue,
  stepByDivision,
  parseCapacity,
  roundToDivision
} from '../../utils/divisionUtils';

const DivisionInput = ({
  value,
  onChange,
  testLoad,
  division,
  unit,
  capacity,
  disabled = false,
  size = 'small',
  label = '',
  tabIndex,  // Tab index for keyboard navigation
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1200px)'); // Tablets get mobile UI
  const [showPicker, setShowPicker] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hasUserTyped, setHasUserTyped] = useState(false);

  // Parse division and capacity
  const divisionNum = useMemo(() => parseDivision(division), [division]);
  const unitStr = useMemo(() => unit || parseUnit(division) || 'lb', [unit, division]);
  const capacityNum = useMemo(() => parseCapacity(capacity), [capacity]);

  // Format test load as hint
  const hintValue = useMemo(() =>
    formatToDivision(testLoad, division),
    [testLoad, division]
  );

  // Check if current value is valid
  const isValid = useMemo(() => {
    if (localValue === '' || localValue === null) return true;
    return isValidDivisionValue(parseFloat(localValue), division);
  }, [localValue, division]);

  // Sync external value to local state
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
      setLocalValue(String(value));
      setHasUserTyped(true);
    } else {
      setLocalValue('');
      setHasUserTyped(false);
    }
  }, [value]);

  // Handle manual input change
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    setHasUserTyped(newValue !== '');

    // Still call onChange even for non-valid values (for typing experience)
    onChange(newValue);
  }, [onChange]);

  // Handle blur - round to nearest division if invalid
  const handleBlur = useCallback(() => {
    setIsFocused(false);

    if (localValue === '' || localValue === null) {
      return;
    }

    const numValue = parseFloat(localValue);
    if (isNaN(numValue)) {
      setLocalValue('');
      onChange('');
      return;
    }

    // If not valid, round to nearest division
    if (!isValidDivisionValue(numValue, division)) {
      const rounded = roundToDivision(numValue, division);
      const clamped = Math.max(0, Math.min(capacityNum, rounded));
      const formatted = formatToDivision(clamped, division);
      setLocalValue(formatted);
      onChange(formatted);
    }
  }, [localValue, division, capacityNum, onChange]);

  // Handle focus
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  // Handle step up/down
  const handleStep = useCallback((direction) => {
    const newValue = stepByDivision(localValue, testLoad, division, capacityNum, direction);
    setLocalValue(newValue);
    setHasUserTyped(true);
    onChange(newValue);
  }, [localValue, testLoad, division, capacityNum, onChange]);

  // Handle mobile click to open picker
  const handleClick = useCallback((e) => {
    if (isMobile && !disabled) {
      e.preventDefault();
      setShowPicker(true);
    }
  }, [isMobile, disabled]);

  // Handle picker selection
  const handlePickerSelect = useCallback((selectedValue) => {
    setLocalValue(selectedValue);
    setHasUserTyped(true);
    onChange(selectedValue);
  }, [onChange]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleStep(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleStep(-1);
    }
  }, [handleStep]);

  // Determine what to show
  const displayValue = hasUserTyped ? localValue : '';
  const showHint = !hasUserTyped && !isFocused;

  // Mobile: render inline MobileTapStepInput
  if (isMobile) {
    return (
      <Box sx={{ ...sx }}>
        <MobileTapStepInput
          value={localValue}
          onChange={(newValue) => {
            setLocalValue(String(newValue));
            setHasUserTyped(newValue !== '');
            onChange(newValue);
          }}
          testLoad={testLoad}
          division={division}
          unit={unitStr}
          capacity={capacityNum}
          disabled={disabled}
          label={label}
          divisionNum={divisionNum}
        />
      </Box>
    );
  }

  // Desktop: existing layout with arrows + TextField + DivisionScrollPicker
  return (
    <Box sx={{
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      ...sx
    }}>
      {/* Up Arrow (Desktop only) */}
      <IconButton
        size="small"
        onClick={() => handleStep(1)}
        disabled={disabled}
        sx={{
          p: 0.25,
          '&:hover': { bgcolor: 'action.hover' }
        }}
        tabIndex={-1}
        aria-label="Increase by one division"
      >
        <KeyboardArrowUp fontSize="small" />
      </IconButton>

      {/* Input Field */}
      <Box sx={{ position: 'relative' }}>
        <TextField
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          size={size}
          inputProps={{
            inputMode: 'decimal',
            step: divisionNum,
            tabIndex: tabIndex,  // Pass through tab index for keyboard navigation
            'aria-label': label || `Reading input, division ${divisionNum} ${unitStr}`,
            style: {
              textAlign: 'center',
              minWidth: '70px',
              paddingRight: isValid ? undefined : '24px'
            }
          }}
          placeholder={showHint ? hintValue : undefined}
          sx={{
            width: 100,
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(0, 0, 0, 0.4)',
              opacity: 1
            },
            '& .MuiOutlinedInput-root': {
              ...((!isValid && hasUserTyped) && {
                '& fieldset': {
                  borderColor: theme.palette.warning.main,
                  borderWidth: 2
                }
              })
            }
          }}
          {...props}
        />

        {/* Warning Icon for invalid value */}
        {!isValid && hasUserTyped && (
          <Tooltip
            title={`Value will be rounded to ${formatToDivision(roundToDivision(parseFloat(localValue) || 0, division), division)} ${unitStr}`}
            placement="top"
          >
            <WarningIcon
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '1rem',
                color: theme.palette.warning.main,
                cursor: 'help'
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Down Arrow (Desktop only) */}
      <IconButton
        size="small"
        onClick={() => handleStep(-1)}
        disabled={disabled}
        sx={{
          p: 0.25,
          '&:hover': { bgcolor: 'action.hover' }
        }}
        tabIndex={-1}
        aria-label="Decrease by one division"
      >
        <KeyboardArrowDown fontSize="small" />
      </IconButton>

      {/* Desktop Scroll Picker (kept for future use) */}
      <DivisionScrollPicker
        open={showPicker}
        value={localValue || ''}
        testLoad={testLoad}
        division={division}
        capacity={capacityNum}
        unit={unitStr}
        onSelect={handlePickerSelect}
        onClose={() => setShowPicker(false)}
      />
    </Box>
  );
};

export default DivisionInput;
